/**
 * JAISIU-3655 — in-thread search highlight + match-counter navigation.
 *
 * The chat-thread search input filters the visible transcript to messages
 * that match the query (filter logic lives in `views/chat.ts`). This
 * module is the second half: it walks the rendered DOM, wraps every
 * occurrence of the query in `<mark class="chat-search-hit">`, and
 * exposes helpers to cycle between the matches with smooth-scroll.
 *
 * ## Design notes
 *
 * - Pure DOM helpers, no Lit, no JSX. The host (`app.ts`) calls
 *   `highlightMatchesIn()` from its `updated()` lifecycle after the chat
 *   view re-renders. The chat view itself is just a render function that
 *   returns a lit-html template; the only Lit-native surface we touch is
 *   the `data-chat-search-current` attribute on the thread container,
 *   which we read to decide which mark to mark `.chat-search-hit--active`.
 *
 * - Idempotent. Calling `highlightMatchesIn()` on a container that
 *   already has marks for the same query is a no-op; changing the query
 *   clears the previous marks before re-wrapping. We never leave stale
 *   `<mark>` nodes behind after the query is cleared.
 *
 * - We deliberately skip wrapping inside `chat-thread-search` itself
 *   (the input + counter UI). The container the operator types into is
 *   a sibling of `.chat-thread`, so its text is naturally excluded.
 *
 * - The match list is always in DOCUMENT ORDER — `collectMatchElements`
 *   walks the subtree the same way `TreeWalker` does. Index 0 is the
 *   first hit at the top of the transcript; index `total - 1` is the
 *   last hit at the bottom. ↑/↓ cycle by `±1 mod total`.
 *
 * - Navigation scroll is bracket-external: callers wrap
 *   `scrollToMatch(...)` in `withProgrammaticScroll(host, ...)` so the
 *   resulting `scroll` event is NOT classified as a user gesture and
 *   never feeds the persisted scroll anchor. This keeps the
 *   "JAISIU-3533 last-read position" invariant intact — paging through
 *   search hits does not push the operator's saved anchor row.
 */

const HIGHLIGHT_CLASS = "chat-search-hit";
const ACTIVE_CLASS = "chat-search-hit--active";
const SKIP_SELECTOR = ".chat-thread-search";
const MAX_HITS = 5000;

/** A regex that matches the query anywhere in a string, case-insensitive.
 *
 * Whitespace handling — leading and trailing whitespace in the query
 * is SIGNIFICANT: a query like "can " (trailing space) is required
 * to be followed by whitespace in the matched text, so it does NOT
 * match rows that contain only "can" as a bare word. This matches
 * the operator's request that the search engine "take spaces into
 * account" — a typo with a stray space at the end should narrow the
 * result set, not silently widen it. Internal whitespace between
 * tokens still uses `\s+` so a query like "can do" matches a row
 * whose rendered prose has a newline or tab between the words.
 *
 * Pure-whitespace queries ("   ") short-circuit to null — the caller
 * treats them as empty. The empty-after-trim gate is unchanged from
 * the previous contract; what changed is that a query with content
 * + leading or trailing whitespace is now distinct from the
 * whitespace-stripped version.
 *
 * Exported so the visible-message filter in `views/chat.ts` can use
 * the SAME regex semantics. Before this export the filter did
 * `text.includes(searchQLiteral)` after collapsing whitespace, which
 * silently dropped the trailing-space requirement — "can " matched
 * rows that only contained "can" as a bare word, so the highlight
 * pass (which DOES require trailing whitespace) found no marks and
 * the operator saw the counter jump but no yellow underline.
 */
export function buildSearchRegex(query: string): RegExp | null {
  if (!query) return null;
  const leadingMatch = query.match(/^\s+/);
  const trailingMatch = query.match(/\s+$/);
  const leading = leadingMatch ? leadingMatch[0] : "";
  const trailing = trailingMatch ? trailingMatch[0] : "";
  const middle = query.slice(leading.length, query.length - trailing.length);
  const tokens = middle.split(/\s+/).filter((t) => t.length > 0);
  // Pure whitespace query — treat as empty.
  if (tokens.length === 0) return null;
  const escapeToken = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, (m) => "\\" + m);
  // JAISIU-3655 (followup) — the previous implementation joined
  // with `\s+` AND pushed `\s+` parts for every leading/trailing
  // gap, doubling each gap. For a query of `"can "` the emitted
  // regex was `/can\s+\s+/gi`, requiring two trailing whitespace
  // chars instead of one. Rows like `"you can do it"` therefore
  // failed to match — the operator's "can " bug.
  //
  // New shape: token bodies always carry a `\s+` between them so
  // "can do" matches across newlines; the leading/trailing parts
  // supply the gap at the borders; the join is `""` so we never
  // accidentally emit two `\s+` in a row.
  const escapedTokens: string[] = tokens.map(escapeToken);
  const body = escapedTokens.join("\\s+");
  const parts: string[] = [];
  if (leading) parts.push("\\s+");
  if (body) parts.push(body);
  if (trailing) parts.push("\\s+");
  return new RegExp(parts.join(""), "gi");
}

/**
 * True when `node` lives inside (or is itself) a region that must be
 * skipped — currently the search input/counter UI, and (by default)
 * the assistant-turn activity section.
 *
 * The activity skip is opt-IN via `searchInActivities = false` —
 * the operator's default. When OFF we never paint a highlight on
 * text inside `[data-testid="chat-turn-activity"]` (the collapsible
 * "▸ Activity · N steps" section of an assistant turn that holds
 * tool calls, command outputs, and reasoning chunks). The visible
 * chat-text and the final answer are still highlighted. The toggle
 * is surfaced through a gear-icon popover next to the in-thread
 * nav (and next to the × in the sidebar search) — see
 * `views/chat.ts` and `views/fleet-chat-history-sidebar.ts`.
 *
 * We never highlight matches inside the search box itself
 * regardless of the toggle — that's the unconditional skip.
 */
function isSkipped(node: Node, searchInActivities: boolean): boolean {
  let cur: Node | null = node;
  while (cur) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const el = cur as Element;
      if (el.classList && el.classList.contains("chat-thread-search")) {
        return true;
      }
      if (!searchInActivities) {
        if (
          el.matches?.('[data-testid="chat-turn-activity"]') ||
          ((el as HTMLElement).dataset && (el as HTMLElement).dataset["turnActivity"] !== undefined)
        ) {
          return true;
        }
      }
    }
    cur = cur.parentNode;
  }
  return false;
}

/**
 * Recursively walk text nodes and wrap query matches in `<mark>` tags.
 *
 * Existing marks are unwrapped first so a query change cleanly replaces
 * the previous highlight set. Returns the new set of marks in document
 * order. The caller (the lifecycle hook in `app.ts`) keeps its own
 * reference to the returned array to power the counter.
 *
 * The pass runs in TWO phases:
 *
 * 1. **Single-text-node pass** — the original fast path. The regex is
 *    applied to each non-empty text node independently and matches
 *    are wrapped in `<mark>`. Covers the common case (a query that
 *    fits inside one text node).
 *
 * 2. **Cross-text-node pass** — for queries that didn't match in
 *    phase 1, we concatenate the text of every text node inside each
 *    message row (`[data-msg-id]`), run the regex on the merged
 *    string, and map the resulting character offsets back to the
 *    individual text nodes, splitting them at the match boundaries
 *    and wrapping the matched range. This is what makes "That claim
 *    was wrong. The" highlight correctly when the source row's
 *    content is split across multiple inline elements (e.g. a
 *    sentence ending in plain text followed by a `<code>` block
 *    that opens with "The"). Without this phase the operator would
 *    see the counter at 1/1 but no yellow underline, and the
 *    active-match scroll would never fire because no `<mark>` ever
 *    exists to anchor it.
 */
export function highlightMatchesIn(
  root: HTMLElement,
  query: string,
  options: { searchInActivities?: boolean } = {},
): HTMLElement[] {
  clearHighlights(root);
  const re = buildSearchRegex(query);
  if (!re) return [];
  const searchInActivities = options.searchInActivities === true;

  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (isSkipped(node, searchInActivities)) return NodeFilter.FILTER_REJECT;
      // Skip empty text nodes (TreeWalker will still visit them in some
      // browsers — guarding here keeps the loop below tight).
      const text = node.nodeValue ?? "";
      if (!text) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes: Text[] = [];
  let n: Node | null = walker.nextNode();
  while (n) {
    textNodes.push(n as Text);
    n = walker.nextNode();
    if (textNodes.length > MAX_HITS * 4) break; // safety stop
  }

  const marks: HTMLElement[] = [];

  for (const textNode of textNodes) {
    const text = textNode.nodeValue ?? "";
    re.lastIndex = 0;
    let match: RegExpExecArray | null;
    let cursor = 0;
    const fragments: (string | HTMLElement)[] = [];
    let matchedAny = false;

    while ((match = re.exec(text)) !== null) {
      matchedAny = true;
      const start = match.index;
      const end = start + match[0].length;
      if (start > cursor) {
        fragments.push(text.slice(cursor, start));
      }
      const mark = root.ownerDocument.createElement("mark");
      mark.className = HIGHLIGHT_CLASS;
      mark.textContent = text.slice(start, end);
      mark.setAttribute("data-chat-search-hit", String(marks.length));
      fragments.push(mark);
      marks.push(mark);
      cursor = end;
      if (match[0].length === 0) re.lastIndex++; // safety: zero-width match
    }

    if (!matchedAny) continue;

    if (cursor < text.length) {
      fragments.push(text.slice(cursor));
    }

    const parent = textNode.parentNode;
    if (!parent) continue;
    const docFrag = root.ownerDocument.createDocumentFragment();
    for (const f of fragments) {
      docFrag.appendChild(typeof f === "string" ? root.ownerDocument.createTextNode(f) : f);
    }
    parent.replaceChild(docFrag, textNode);
  }

  // Phase 2 — cross-text-node pass. Runs only if phase 1 found
  // nothing, so the common case stays on the fast path. We scope
  // the merge to message rows (`[data-msg-id]`) so the result
  // mirrors the host's `chatMessageSearchMatchIds` array (which
  // is also keyed by row).
  if (marks.length === 0) {
    highlightAcrossTextNodesInRows(root, re, marks, searchInActivities);
  }

  return marks;
}

/**
 * Phase-2 helper. For each `[data-msg-id]` row inside `root`, build
 * a merged-text view of all (non-skipped) text nodes in document
 * order, run `re` on the merged string, and split-wrap the matched
 * ranges. `marks` is mutated in place — newly created `<mark>`
 * elements are appended and their index in the overall marks array
 * is recorded via the `data-chat-search-hit` attribute.
 *
 * The previous contract was one match per row. That disagreed with
 * the walker's "one entry per occurrence" model when a row had
 * multiple occurrences (e.g. a quoted line with several "can "
 * substrings): the counter reported N matches but only the first
 * was painted as a `<mark>`. This helper now finds ALL occurrences
 * in each row's merged text and wraps each separately.
 */
function highlightAcrossTextNodesInRows(
  root: HTMLElement,
  re: RegExp,
  marks: HTMLElement[],
  searchInActivities: boolean,
): void {
  const doc = root.ownerDocument;
  const rows = root.querySelectorAll<HTMLElement>("[data-msg-id]");
  if (rows.length === 0) return;
  for (const row of rows) {
    const rowTextNodes: Text[] = [];
    const rowWalker = doc.createTreeWalker(row, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (isSkipped(node, searchInActivities)) return NodeFilter.FILTER_REJECT;
        const t = node.nodeValue ?? "";
        if (!t) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let cur: Node | null = rowWalker.nextNode();
    while (cur) {
      rowTextNodes.push(cur as Text);
      cur = rowWalker.nextNode();
      if (rowTextNodes.length > MAX_HITS * 4) break;
    }
    if (rowTextNodes.length < 2) continue; // single-node rows already handled in phase 1
    // Build a merged string with parallel offsets so we can map a
    // match position back to (textNode, offsetWithinNode).
    let merged = "";
    const segments: Array<{ node: Text; startInMerged: number; endInMerged: number }> = [];
    for (const tn of rowTextNodes) {
      const t = tn.nodeValue ?? "";
      const startInMerged = merged.length;
      merged += t;
      segments.push({ node: tn, startInMerged, endInMerged: merged.length });
    }
    // Find every match in this row. We collect them first and
    // apply replacements in REVERSE document order so the
    // earlier `replaceChild` calls do not invalidate the later
    // text-node references (each replaceChild detaches the
    // target node from the parent; the previous version applied
    // a single replacement and walked off, but with multiple
    // matches per row we must process the highest-offset match
    // first to keep earlier offsets stable).
    type Repl = {
      startSegIndex: number;
      endSegIndex: number;
      startOffsetInStartSeg: number;
      endOffsetInEndSeg: number;
    };
    const allRepls: Repl[] = [];
    re.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(merged)) !== null) {
      const matchStart = match.index;
      const matchEnd = matchStart + match[0].length;
      const startSeg = findSegment(segments, matchStart);
      const endSeg = findSegment(segments, Math.max(matchStart, matchEnd - 1));
      if (startSeg == null || endSeg == null) {
        // Avoid infinite loops on degenerate segments.
        if (match[0].length === 0) re.lastIndex++;
        continue;
      }
      const startSegIndex = segments.indexOf(startSeg);
      const endSegIndex = segments.indexOf(endSeg);
      allRepls.push({
        startSegIndex,
        endSegIndex,
        startOffsetInStartSeg: matchStart - startSeg.startInMerged,
        endOffsetInEndSeg: matchEnd - endSeg.startInMerged,
      });
      if (match[0].length === 0) re.lastIndex++;
    }
    if (allRepls.length === 0) continue;
    // Apply in reverse document order (highest startSegIndex first)
    // so that earlier segments keep their original references.
    allRepls.sort((a, b) => b.startSegIndex - a.startSegIndex);
    for (const repl of allRepls) {
      for (let i = repl.endSegIndex; i >= repl.startSegIndex; i--) {
        const seg = segments[i];
        if (!seg) continue;
        const text = seg.node.nodeValue ?? "";
        const begin = i === repl.startSegIndex ? repl.startOffsetInStartSeg : 0;
        const end =
          i === repl.endSegIndex ? repl.endOffsetInEndSeg : seg.endInMerged - seg.startInMerged;
        const before = text.slice(0, begin);
        const matched = text.slice(begin, end);
        const after = text.slice(end);
        const parent = seg.node.parentNode;
        if (!parent) continue;
        const frag = doc.createDocumentFragment();
        if (before) frag.appendChild(doc.createTextNode(before));
        if (matched) {
          const mark = doc.createElement("mark");
          mark.className = HIGHLIGHT_CLASS;
          mark.textContent = matched;
          mark.setAttribute("data-chat-search-hit", String(marks.length));
          marks.push(mark);
          frag.appendChild(mark);
        }
        if (after) frag.appendChild(doc.createTextNode(after));
        parent.replaceChild(frag, seg.node);
        // `seg.node` is now detached. Zero out its range so any
        // subsequent repl that still references it (e.g. when the
        // operator re-runs the highlight pass before the next
        // commit) does not see stale offsets.
        seg.startInMerged = -1;
        seg.endInMerged = -1;
      }
    }
  }
}

/** Binary-search the segments array for the segment containing
 * `offset`. Returns the segment whose `[startInMerged, endInMerged)`
 * range contains the offset, or null if no segment does (only
 * possible when `offset` is past the last segment's end). */
function findSegment(
  segments: Array<{ node: Text; startInMerged: number; endInMerged: number }>,
  offset: number,
): (typeof segments)[number] | null {
  let lo = 0;
  let hi = segments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const seg = segments[mid];
    if (!seg) return null;
    if (offset < seg.startInMerged) {
      hi = mid - 1;
    } else if (offset >= seg.endInMerged) {
      lo = mid + 1;
    } else {
      return seg;
    }
  }
  return null;
}

/**
 * Remove every `<mark class="chat-search-hit">` from `root` and
 * concatenate its children back into the surrounding text. Safe to
 * call on a container with no marks (no-op).
 */
export function clearHighlights(root: HTMLElement): void {
  const marks = root.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`);
  for (const mark of Array.from(marks)) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
    parent.normalize?.();
  }
}

/**
 * Return the current set of highlighted matches in document order.
 * Used by the host when the lifecycle hook sees the query is unchanged
 * but content scrolled (e.g. streaming appended more rows) so the
 * counter can stay in sync.
 */
export function collectMatchElements(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(`mark.${HIGHLIGHT_CLASS}`);
  return Array.from(nodes);
}

/**
 * Mark `index` in `marks` as the active hit (others lose the
 * `--active` modifier). Returns the active element so the caller can
 * scroll it into view. `index` is clamped to the valid range; passing
 * an out-of-range index is a no-op (returns null).
 */
export function setActiveMatch(marks: HTMLElement[], index: number): HTMLElement | null {
  if (marks.length === 0) return null;
  const safeIndex = ((index % marks.length) + marks.length) % marks.length;
  for (let i = 0; i < marks.length; i++) {
    const el = marks[i];
    if (i === safeIndex) {
      el.classList.add(ACTIVE_CLASS);
    } else {
      el.classList.remove(ACTIVE_CLASS);
    }
  }
  return marks[safeIndex] ?? null;
}

/**
 * Smooth-scroll the active match into view. The caller MUST wrap this
 * call in `withProgrammaticScroll(host, ...)` — this helper does not
 * own the flag because the host owns lifecycle. Returns the resolved
 * scroll target (the match element, or null if missing).
 *
 * If the match is already inside the container's visible viewport
 * (within a 4 px margin to absorb sub-pixel scroll positions) we
 * skip the scroll entirely — the active highlight paints without
 * any layout shift. This matches the operator's spec that clicking
 * the in-thread nav "should not move the screen" when every
 * match is already on screen.
 */
export function scrollToMatch(
  matchEl: HTMLElement | null,
  container: HTMLElement,
): HTMLElement | null {
  if (!matchEl) return null;
  const containerRect = container.getBoundingClientRect();
  const matchRect = matchEl.getBoundingClientRect();
  // JAISIU-3655 (followup) — viewport-intersection check. Treat the
  // match as "already visible" if its top is at least 4 px below
  // the container's top edge AND its bottom is at least 4 px above
  // the bottom edge. The 4 px buffer absorbs sub-pixel scroll
  // rounding and avoids nudging the viewport when the match sits
  // right on the edge (which would otherwise feel like a flicker
  // when the operator toggles the active match between two
  // side-by-side phrases).
  const VISIBLE_MARGIN = 4;
  const topInView =
    matchRect.top >= containerRect.top + VISIBLE_MARGIN &&
    matchRect.bottom <= containerRect.bottom - VISIBLE_MARGIN;
  if (topInView) {
    return matchEl;
  }
  // Compute the match's offset relative to the scroll container so we
  // can place it comfortably in view without ever changing the
  // "saved scrollTop" the scroll-anchor system reads on session
  // switch. `scrollIntoView` would do this, but it also focuses the
  // element (which steals selection from the search input), and the
  // behavior across browsers for `block: "center"` vs `"nearest"` is
  // inconsistent. Doing the math ourselves keeps the scroll target
  // behavior stable and avoids the focus side-effect.
  const offsetTop = container.scrollTop + (matchRect.top - containerRect.top);
  const targetTop = offsetTop - Math.max(0, (container.clientHeight - matchRect.height) / 2);

  container.scrollTo({
    top: Math.max(0, targetTop),
    behavior: "smooth",
  });
  return matchEl;
}
